import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { Observable, of } from 'rxjs';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { ClientService } from '../../../core/api/client.service';
import { Client, ClientList } from '../../../core/models/client.model';

/**
 * CVA-обёртка над `<mat-autocomplete>` для поиска клиента.
 *
 * Value: `number | null` (client_id).
 *
 * Внутри два независимых `FormControl`:
 * - `control` — основной, хранит id клиента (это и есть значение CVA);
 * - `searchControl` — строка, которую видит пользователь в `<input>` (ФИО+телефон
 *   выбранного клиента, или то, что он печатает при поиске).
 *
 * Подход без `displayWith`:
 * - При выборе опции `searchControl.patchValue(formatClient(c))` (Material без
 *   `displayWith` подставил бы строковое представление объекта — поэтому патчим
 *   сами), затем `control.patchValue(c.id)` (триггерит `onChange` через подписку).
 * - При `writeValue(id)` — подгружаем клиента через `ClientService.get(id)` и
 *   заполняем `searchControl` его ФИО+телефон.
 *
 * Показывает server-ошибку, которую бэкенд прислал на `client` (через
 * `parentErrors?.['serverError']` — аналогично `car-select`/`datetime-input`).
 *
 * Кнопка «Новый клиент» (`routerLink="/clients/new"`) намеренно живёт внутри
 * компонента по требованию пользователя.
 */
@Component({
  selector: 'app-client-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
  ],
  templateUrl: './client-select.component.html',
  styleUrl: './client-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ClientSelectComponent),
    },
  ],
})
export class ClientSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = 'Поиск клиента';
  @Input() required = false;
  @Input() placeholder = 'Введите имя или телефон';

  /** Хранит id выбранного клиента — это и есть значение CVA. */
  readonly control = new FormControl<number | null>(null);
  /** Строка, которую видит пользователь в `<input>`. */
  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  filteredClients$: Observable<ClientList[]> = of([]);

  onChange: (v: number | null) => void = () => {};
  onTouched: () => void = () => {};

  private readonly injector = inject(Injector);
  private readonly clientService = inject(ClientService);
  private ngControl: NgControl | null = null;

  private valueSub = this.control.valueChanges.subscribe((v) => {
    this.onChange(v);
  });

  private get parentErrors(): Record<string, any> | null {
    const control = this.ngControl?.control as FormControl | null;
    return control?.errors ?? null;
  }

  ngOnInit(): void {
    // Отложенное получение NgControl — разрывает цикл DI (см. car-select/datetime-input).
    try {
      this.ngControl = this.injector.get(NgControl, null, { self: true });
    } catch {
      this.ngControl = null;
    }
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.valueSub.unsubscribe();
  }

  // ── CVA-методы ────────────────────────────────────────────────────
  writeValue(value: number | null): void {
    this.control.patchValue(value, { emitEvent: false });
    if (value == null) {
      this.searchControl.patchValue('', { emitEvent: false });
      return;
    }
    // Подгружаем клиента, чтобы отобразить ФИО+телефон в инпуте.
    this.clientService.get(value).subscribe({
      next: (client: Client) => {
        // Не патчим, если за время запроса значение уже изменилось — иначе
        // перезапишем свежий ввод пользователя.
        if (this.control.value === value) {
          this.searchControl.patchValue(this.formatClient(client), { emitEvent: false });
        }
      },
      error: () => {
        if (this.control.value === value) {
          this.searchControl.patchValue('', { emitEvent: false });
        }
      },
    });
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
      this.searchControl.enable({ emitEvent: false });
    }
  }

  // ── View helpers ─────────────────────────────────────────────────
  showServerError(): boolean {
    const errors = this.parentErrors;
    if (!errors) return false;
    return !!errors['serverError'];
  }

  serverError(): string {
    const errors = this.parentErrors;
    if (!errors) return '';
    return errors['serverError'] ? String(errors['serverError']) : '';
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const c = event.option.value as ClientList;
    // `emitEvent: false`, чтобы не триггерить повторный поиск по ФИО+телефону.
    this.searchControl.patchValue(this.formatClient(c), { emitEvent: false });
    this.control.patchValue(c.id);
    this.onTouched();
  }

  // ── Internal ─────────────────────────────────────────────────────
  private setupSearch(): void {
    this.filteredClients$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((val: string) => {
        const q = (val || '').trim();
        if (q.length < 2) return of([]);
        return this.clientService.search(q).pipe(map(res => res.results));
      }),
    );
  }

  /** "Фамилия Имя Отчество (телефон)" — без лишнего пробела при пустом отчестве. */
  private formatClient(c: { last_name: string; first_name: string; middle_name: string; phone: string }): string {
    const m = (c.middle_name ?? '').trim();
    return m
      ? `${c.last_name} ${c.first_name} ${m} (${c.phone})`
      : `${c.last_name} ${c.first_name} (${c.phone})`;
  }
}
